import { fullyEncode } from '@utils/url';
import {
  MecProduct,
  MecSize,
  MecVariant,
  MECProductColour,
} from '@adapters/bigcommerce/types/product';
import { PRODUCT_IMG_UNAVAILABLE, SCHEMA_ORG, NO_COLOUR } from './index';
import dayjs from 'dayjs';

export const buildVariantUrl = (
  canonicalUrl?: string,
  colour?: string,
  size?: string | null
) => {
  if (!canonicalUrl) return null;

  const queryParams = [
    colour && `colour=${colour}`,
    size && `size=${size}`,
  ].filter(Boolean);

  return fullyEncode(
    `${canonicalUrl}${queryParams.length ? '?' : ''}${queryParams.join('&')}`
  );
};

export const getAvailabilitySchema = ({
  availableForSaleOnline,
  isInStock,
  isDiscontinued,
}: {
  availableForSaleOnline: boolean;
  isInStock: boolean;
  isDiscontinued: boolean;
}) => {
  if (isDiscontinued) {
    return `${SCHEMA_ORG}Discontinued`;
  }
  if (!availableForSaleOnline && isInStock) {
    return `${SCHEMA_ORG}InStoreOnly`;
  }

  return availableForSaleOnline
    ? `${SCHEMA_ORG}InStock`
    : `${SCHEMA_ORG}OutOfStock`;
};

export const getVariantName = (
  productName?: string,
  colourName?: string,
  sizeName?: string
): string => {
  return [productName, colourName, sizeName].filter(Boolean).join(' - ');
};

export const getVariantImage = (
  variant: MecVariant,
  placeholderImage: string
): string => {
  return variant.defaultImage?.urlOriginal?.includes(PRODUCT_IMG_UNAVAILABLE) ||
    !variant.defaultImage?.urlOriginal
    ? placeholderImage
    : variant.defaultImage?.urlOriginal;
};

export const getVariantColourAndSize = (
  variant: MecVariant,
  colourVariants?: MECProductColour[],
  sizeVariants?: MecSize[]
) => {
  const variantColour = colourVariants?.find(
    (colour) =>
      String(colour.id) === String(variant.colourId) &&
      colour.name !== NO_COLOUR
  );

  const variantSize = sizeVariants?.find(
    (size) => String(size.id) === String(variant.sizeId)
  );

  return { variantColour, variantSize };
};

export const createVariantOffer = (
  variant: MecVariant,
  variantUrl: string,
  variantAvailability: string,
  productOutOfStock = false
) => {
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');

  return {
    '@type': 'Offer',
    itemCondition: `${SCHEMA_ORG}NewCondition`,
    seller: {
      '@type': 'Organization',
      name: 'MEC',
    },
    availability: variantAvailability,
    url: variantUrl,
    // Omit pricing from the schema when the product is out of stock.
    ...(!productOutOfStock && {
      priceCurrency: variant.price?.price?.currencyCode,
      price: variant.price?.price?.value,
      priceValidUntil: variant.price?.isoEndDate ?? tomorrow,
    }),
  };
};

export interface VariantMappingParams {
  variant: MecVariant;
  product: MecProduct;
  canonicalUrl?: string;
  colourVariants?: MECProductColour[];
  sizeVariants?: MecSize[];
  placeholderImage: string;
  productOutOfStock?: boolean;
}

export const mapVariantToSchema = ({
  variant,
  product,
  canonicalUrl,
  colourVariants,
  sizeVariants,
  placeholderImage,
  productOutOfStock = false,
}: VariantMappingParams) => {
  const variantAvailability = getAvailabilitySchema({
    availableForSaleOnline: variant?.availableForSaleOnline || false,
    isInStock: variant?.hasStockAvailable || false,
    isDiscontinued: false,
  });

  const { variantColour, variantSize } = getVariantColourAndSize(
    variant,
    colourVariants,
    sizeVariants
  );

  const variantUrl = buildVariantUrl(
    canonicalUrl,
    variantColour?.name,
    variantSize?.name
  );

  const variantName = getVariantName(
    product?.name,
    variantColour?.name,
    variantSize?.name || ''
  );

  const variantImage = getVariantImage(variant, placeholderImage);

  const variantOffer = createVariantOffer(
    variant,
    variantUrl || '',
    variantAvailability,
    productOutOfStock
  );

  return {
    '@context': SCHEMA_ORG,
    '@type': 'Product',
    name: variantName,
    image: variantImage,
    description: `${variantName} : ${product?.description}`,
    url: variantUrl,
    sku: variant.sku,
    ...(variantColour?.name && { color: variantColour?.name }),
    ...(variantSize?.name && { size: variantSize?.name }),
    offers: variantOffer,
  };
};
